import { sumFeedTargetConsumptionLbsPerDay } from './feedConsumptionService';

const DAY_MS = 24 * 60 * 60 * 1000;

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeString(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function filterFeedEventsByType(events = [], feedType) {
  const normalizedFilter = normalizeString(feedType);
  if (!normalizedFilter || normalizedFilter === 'all') return [...events];
  return events.filter((event) => normalizeString(event?.feed_type) === normalizedFilter);
}

export function normalizeDeerSightingsByDate(sightings = []) {
  const map = new Map();
  sightings.forEach((entry) => {
    const date = normalizeDate(entry?.date);
    if (!date) return;
    const count = Number(entry?.total_deer_seen ?? entry?.count);
    if (!Number.isFinite(count)) return;
    map.set(date, count);
  });
  return map;
}

export function buildAnalyticsSeries({
  feedEvents = [],
  deerSightings = [],
  feedType = 'all',
  rangeStart,
  rangeEnd
}) {
  const filtered = filterFeedEventsByType(feedEvents, feedType);
  const startMs = rangeStart ? new Date(rangeStart).getTime() : null;
  const endMs = rangeEnd ? new Date(rangeEnd).getTime() : null;
  const feedTotals = new Map();
  filtered.forEach((event) => {
    const date = normalizeDate(event?.date || event?.recorded_at);
    if (!date) return;
    const time = new Date(date).getTime();
    if ((startMs && time < startMs) || (endMs && time > endMs)) return;
    const amount = Number(event?.amount_lbs);
    if (!Number.isFinite(amount)) return;
    const current = feedTotals.get(date) || 0;
    feedTotals.set(date, current + amount);
  });
  const deerMap = new Map();
  normalizeDeerSightingsByDate(deerSightings).forEach((count, date) => {
    const time = new Date(date).getTime();
    if ((startMs && time < startMs) || (endMs && time > endMs)) return;
    deerMap.set(date, count);
  });
  const dateSet = new Set();
  feedTotals.forEach((_, date) => dateSet.add(date));
  deerMap.forEach((_, date) => dateSet.add(date));
  return Array.from(dateSet)
    .sort((a, b) => (a < b ? -1 : 1))
    .map((date) => ({
      date,
      feedLbs: feedTotals.get(date) || 0,
      deerCount: deerMap.get(date) || 0
    }));
}

function normalizeRangeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const normalized = new Date(date.getTime());
  normalized.setUTCHours(0, 0, 0, 0);
  return normalized;
}

function buildDescendingDates(startDate, endDate) {
  const dates = [];
  for (let ts = endDate.getTime(); ts >= startDate.getTime(); ts -= DAY_MS) {
    dates.push(normalizeDate(ts));
  }
  return dates;
}

export function buildFeedInventorySeries({
  feedEvents = [],
  deerSightings = [],
  feedTargets = [],
  rangeStart,
  rangeEnd
}) {
  if (!Array.isArray(feedTargets) || feedTargets.length === 0) return [];
  const startDate = normalizeRangeDate(rangeStart);
  const endDate = normalizeRangeDate(rangeEnd);
  if (!startDate || !endDate || endDate.getTime() < startDate.getTime()) return [];
  const startKey = normalizeDate(startDate);
  const endKey = normalizeDate(endDate);

  const feedAddsByDate = new Map();
  (Array.isArray(feedEvents) ? feedEvents : []).forEach((event) => {
    const date = normalizeDate(event?.date || event?.recorded_at);
    if (!date) return;
    if ((startKey && date < startKey) || (endKey && date > endKey)) return;
    const amount = Number(event?.amount_lbs);
    if (!Number.isFinite(amount)) return;
    feedAddsByDate.set(date, (feedAddsByDate.get(date) || 0) + amount);
  });

  const deerMap = new Map();
  normalizeDeerSightingsByDate(deerSightings).forEach((count, date) => {
    if ((startKey && date < startKey) || (endKey && date > endKey)) return;
    deerMap.set(date, count);
  });

  const totalRemaining = feedTargets.reduce(
    (sum, target) => sum + (Number(target?.estimatedRemaining) || 0),
    0
  );
  const consumptionPerDay = sumFeedTargetConsumptionLbsPerDay(feedTargets);

  // Walk backward from today's remaining: each prior day +consumption −feed logged that day.
  // Chronological view steps down by combined active-feeder rate; feed logs step inventory up.
  const descendingDates = buildDescendingDates(startDate, endDate);
  const endOfDayByDate = new Map();
  let endInventory = totalRemaining;

  descendingDates.forEach((date) => {
    endOfDayByDate.set(date, Math.max(0, Math.round(endInventory * 10) / 10));
    const feedAdded = feedAddsByDate.get(date) || 0;
    endInventory = endInventory + consumptionPerDay - feedAdded;
    if (endInventory < 0) endInventory = 0;
  });

  return descendingDates
    .slice()
    .reverse()
    .map((date) => ({
      date,
      feedInventoryLbs: endOfDayByDate.get(date) || 0,
      deerCount: deerMap.get(date) || 0
    }));
}

const feedAnalyticsTransforms = {
  filterFeedEventsByType,
  normalizeDeerSightingsByDate,
  buildAnalyticsSeries,
  buildFeedInventorySeries
};

export default feedAnalyticsTransforms;

